import { useQuery } from '@tanstack/react-query'
import {
  CENARIOS,
  percentualPpm,
  ROTULO_CENARIO,
  ROTULO_FATOR,
  type CargaDetalhada,
  type Cenario,
  type ResultadoCenario,
  type SimulacaoResultado,
} from '@via-tax/contracts'
import { Aviso, Cartao, EstadoVazio, Selo } from '@via-tax/ui'
import { useState, type ReactNode } from 'react'
import { useExecutarSimulacao } from '../components/ExecutarSimulacao'
import type { Tela } from '../components/Shell'
import { dataBr, mesCurto, reais, reaisCompacto } from '../formatos'
import type { ContextoSessao } from '../tipos'
import { useTRPC } from '../trpc'

/**
 * Foco 2027 (arquitetura §10, §12.3): a carga de hoje contra a de 2027–2028 com a
 * operação dos últimos 12 meses. Todo número vem com a alíquota, a fonte e a
 * versão ao lado — e a alíquota da CBS de 2027 ainda é estimativa.
 */

/** ppm → "9,3%" (1% = 10.000 ppm) */
const pctPpm = percentualPpm
const sinal = (centavos: number) => (centavos > 0 ? `+${reais(centavos)}` : reais(centavos))

export function Foco2027({ contexto, ir }: { contexto: ContextoSessao; ir: (t: Tela) => void }) {
  const trpc = useTRPC()
  const podeExecutar = (contexto.capacidades as string[]).includes('executar_simulacao')
  const ultima = useQuery({
    ...trpc.simulacao.ultima.queryOptions(),
    refetchInterval: (q) => (q.state.data?.recente?.status === 'PROCESSANDO' ? 3000 : false),
  })
  const recente = ultima.data?.recente
  const concluida = ultima.data?.concluida
  const processando = recente?.status === 'PROCESSANDO'
  const simples = contexto.empresaAtiva?.regimeTributario === 'simples_nacional'
  const execucao = useExecutarSimulacao({ premissasAtuais: concluida?.premissas ?? recente?.premissas, processando, jaRodou: !!concluida, simples, operacoes: concluida?.resultado.operacoes })

  return (
    <div className="flex flex-col gap-5">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Foco 2027</h1>
          <p className="mt-1 max-w-2xl text-sm text-tinta-2">
            Quanto a operação dos últimos 12 meses pagaria de tributo sobre consumo hoje e em 2027–2028, quando a CBS substitui PIS/COFINS e o IBS entra em teste. Simulação para
            decisão — não é apuração.
          </p>
        </div>
        {podeExecutar && execucao.botoes}
      </header>

      {podeExecutar && execucao.painel}

      {processando && <Aviso tom="info">Montando o dossiê e calculando os cenários — a tela atualiza sozinha.</Aviso>}
      {recente?.status === 'ERRO' && <Aviso>A última simulação não terminou: {recente.erro}</Aviso>}

      {ultima.isPending ? (
        <p className="text-sm text-tinta-3">Carregando…</p>
      ) : !concluida ? (
        !processando && (
          <EstadoVazio titulo="Nenhuma simulação ainda">
            {podeExecutar
              ? 'Antes de rodar: envie os XMLs dos últimos 12 meses, aprove a classificação dos produtos que mais vendem e confirme o regime dos maiores fornecedores. O que faltar entra como premissa e aparece marcado.'
              : 'O escritório ainda não rodou a simulação desta empresa.'}
          </EstadoVazio>
        )
      ) : (
        <Resultado r={concluida.resultado} meta={{ criadoPor: concluida.criadoPor, criadoEm: concluida.criadoEm }} ir={ir} staff={podeExecutar} />
      )}
    </div>
  )
}

function Resultado({ r, meta, ir, staff }: { r: SimulacaoResultado; meta: { criadoPor: string; criadoEm: string | Date }; ir: (t: Tela) => void; staff: boolean }) {
  // 2027 tem tela própria (§10.3): a trajetória até 2033 fica no Panorama.
  const anos = [...new Set(r.resultados.map((x) => x.ano))].filter((a) => a <= 2028).sort()
  const [ano, setAno] = useState(anos[0]!)
  const [cenario, setCenario] = useState<Cenario>('BASE')
  const doAno = r.resultados.filter((x) => x.ano === ano)
  const atual = doAno.find((x) => x.cenario === cenario) ?? doAno[0]!

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Abas valor={String(ano)} opcoes={anos.map((a) => ({ valor: String(a), rotulo: String(a) }))} mudar={(v) => setAno(Number(v))} />
        {!atual.simples && <Abas valor={cenario} opcoes={CENARIOS.map((c) => ({ valor: c, rotulo: ROTULO_CENARIO[c] }))} mudar={(v) => setCenario(v as Cenario)} />}
      </div>

      {!atual.simples && <LinhaAliquota c={atual} />}

      <div className="grid gap-3 md:grid-cols-3">
        <Numero rotulo="Carga hoje (regras de 2026)" valor={reais(atual.atual.total)} nota={`${pctPpm(ppmDe(atual.atual.total, atual.receita), 1)} da receita`} />
        <Numero rotulo={`Carga em ${ano}`} valor={reais(atual.nova.total)} nota={`${pctPpm(ppmDe(atual.nova.total, atual.receita), 1)} da receita`} />
        <Numero
          rotulo="Diferença"
          valor={sinal(atual.variacao)}
          nota={`${atual.variacaoSobreReceita > 0 ? '+' : ''}${pctPpm(atual.variacaoSobreReceita)} p.p. sobre a receita de ${reaisCompacto(atual.receita)}`}
          tom={atual.variacao > 0 ? 'alerta' : 'ok'}
        />
      </div>

      <div className="grid gap-5 lg:grid-cols-5">
        <Cartao titulo="O que explica a diferença" className="lg:col-span-3">
          <Cascata c={atual} ano={ano} />
        </Cartao>
        {atual.simples ? (
          <Cartao titulo="Simples Nacional em 2027" className="lg:col-span-2">
            <div className="flex flex-col gap-2 text-sm text-tinta-2">
              <p>
                No Simples a alíquota da CBS não mexe na sua carga: a tabela da LC 214 mantém a alíquota e a dedução de cada faixa e só troca a partilha do DAS — o PIS/COFINS
                vira CBS.
              </p>
              <p>
                O que muda é o seu cliente empresa: ele só credita a CBS/IBS que está dentro do seu DAS —{' '}
                <strong className="num text-tinta">{reais(atual.simples.cbsNoDas + atual.simples.ibsNoDas)}</strong> no ano (
                {pctPpm(ppmDe(atual.simples.cbsNoDas + atual.simples.ibsNoDas, atual.receita))} da receita), contra {pctPpm(atual.aliquotas.cbs + atual.aliquotas.ibs)} de um
                fornecedor do regime regular.
              </p>
              <p>
                A decisão de 2027 é Simples puro × híbrido.{' '}
                <button className="text-navy underline" onClick={() => ir('simples')}>
                  Ver a comparação
                </button>
              </p>
            </div>
          </Cartao>
        ) : (
        <Cartao titulo="Sensibilidade à alíquota da CBS" className="lg:col-span-2">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-tinta-3">
                <th className="pb-2 font-medium">Cenário</th>
                <th className="pb-2 font-medium">CBS</th>
                <th className="pb-2 text-right font-medium">Diferença</th>
              </tr>
            </thead>
            <tbody>
              {doAno.map((x) => (
                <tr key={x.cenario} className={`border-t border-linha ${x.cenario === cenario ? 'font-semibold' : ''}`}>
                  <td className="py-2">{ROTULO_CENARIO[x.cenario]}</td>
                  <td className="num py-2">{pctPpm(x.aliquotas.cbs)}</td>
                  <td className={`num py-2 text-right ${x.variacao > 0 ? 'text-alerta' : 'text-ok'}`}>{sinal(x.variacao)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="mt-3 text-xs text-tinta-3">A faixa vem das projeções publicadas até o Senado fixar a alíquota de referência (prazo: 15/12/2026).</p>
        </Cartao>
        )}
      </div>

      <div className="grid gap-5 lg:grid-cols-5">
        <Cartao titulo="Tributo por tributo" className="lg:col-span-2">
          <Comparativo a={atual.atual} n={atual.nova} simples={atual.simples} ano={ano} />
        </Cartao>
        <Cartao titulo={`CBS/IBS mês a mês em ${ano} (operação de cada mês do ano-base)`} className="lg:col-span-3">
          <Meses c={atual} />
        </Cartao>
      </div>

      <div className="grid gap-5 lg:grid-cols-2">
        <Cartao titulo="Confiabilidade do número">
          <Qualidade r={r} c={atual} />
        </Cartao>
        <Cartao titulo="O que fazer até dezembro">
          <Acoes r={r} c={atual} ir={ir} staff={staff} />
        </Cartao>
      </div>

      <p className="text-xs text-tinta-3">
        Motor {r.proveniencia.versaoEngine} · parâmetros {r.proveniencia.versaoParametros} · base de classificação {r.proveniencia.versaoKb} · calculado em {dataBr(r.proveniencia.calculadoEm)}{' '}
        · pedido por {meta.criadoPor} em {dataBr(meta.criadoEm)}
      </p>
    </div>
  )
}

const ppmDe = (parte: number, todo: number) => (todo ? Math.round((parte * 1_000_000) / todo) : 0)

function Abas({ valor, opcoes, mudar }: { valor: string; opcoes: Array<{ valor: string; rotulo: string }>; mudar: (v: string) => void }) {
  return (
    <div role="tablist" className="inline-flex rounded-md border border-linha bg-painel p-0.5">
      {opcoes.map((o) => (
        <button
          key={o.valor}
          role="tab"
          aria-selected={o.valor === valor}
          onClick={() => mudar(o.valor)}
          className={`rounded px-3 py-1.5 text-sm ${o.valor === valor ? 'bg-navy text-white' : 'text-tinta-2 hover:text-tinta'}`}
        >
          {o.rotulo}
        </button>
      ))}
    </div>
  )
}

function LinhaAliquota({ c }: { c: ResultadoCenario }) {
  const a = c.aliquotas
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border border-linha bg-painel px-4 py-3 text-sm">
      <span>
        CBS <strong className="num">{pctPpm(a.cbs)}</strong> <span className="text-tinta-3">(referência {pctPpm(a.cbsReferencia)} − 0,1 p.p., art. 347)</span>
      </span>
      <span>
        IBS <strong className="num">{pctPpm(a.ibs)}</strong> <span className="text-tinta-3">(0,05% UF + 0,05% município, art. 344)</span>
      </span>
      {a.status === 'ESTIMATIVA' ? <Selo tom="alerta">alíquota estimada</Selo> : <Selo tom="ok">alíquota oficial</Selo>}
      <span className="text-xs text-tinta-3">
        {a.url ? (
          <a className="underline" href={a.url} target="_blank" rel="noreferrer">
            {a.fonte}
          </a>
        ) : (
          a.fonte
        )}
      </span>
    </div>
  )
}

function Numero({ rotulo, valor, nota, tom }: { rotulo: string; valor: string; nota: string; tom?: 'ok' | 'alerta' }) {
  return (
    <div className="rounded-lg border border-linha bg-painel px-4 py-3">
      <p className="text-xs text-tinta-3">{rotulo}</p>
      <p className={`num mt-1 text-2xl font-semibold ${tom === 'alerta' ? 'text-alerta' : tom === 'ok' ? 'text-ok' : 'text-tinta'}`}>{valor}</p>
      <p className="mt-0.5 text-xs text-tinta-2">{nota}</p>
    </div>
  )
}

/** Cascata: carga de hoje → cada fator → carga nova. Barras flutuantes em escala comum. */
function Cascata({ c, ano }: { c: ResultadoCenario; ano: number }) {
  const fatores = c.decomposicao.filter((f) => f.valor !== 0)
  const passos: Array<{ rotulo: string; de: number; ate: number; tipo: 'total' | 'sobe' | 'desce' }> = [{ rotulo: 'Carga hoje', de: 0, ate: c.atual.total, tipo: 'total' }]
  let acumulado = c.atual.total
  for (const f of fatores) {
    passos.push({ rotulo: ROTULO_FATOR[f.fator], de: acumulado, ate: acumulado + f.valor, tipo: f.valor > 0 ? 'sobe' : 'desce' })
    acumulado += f.valor
  }
  passos.push({ rotulo: `Carga em ${ano}`, de: 0, ate: c.nova.total, tipo: 'total' })
  const min = Math.min(0, ...passos.flatMap((p) => [p.de, p.ate]))
  const max = Math.max(1, ...passos.flatMap((p) => [p.de, p.ate]))
  const x = (v: number) => ((v - min) / (max - min)) * 100

  return (
    <div className="flex flex-col gap-1.5">
      {passos.map((p, i) => {
        const esq = x(Math.min(p.de, p.ate))
        const larg = Math.max(0.5, x(Math.max(p.de, p.ate)) - esq)
        const cor = p.tipo === 'total' ? 'bg-navy' : p.tipo === 'sobe' ? 'bg-alerta' : 'bg-ok'
        return (
          <div key={i} className="grid grid-cols-[minmax(0,14rem)_1fr_7rem] items-center gap-3 text-sm">
            <span className={`truncate ${p.tipo === 'total' ? 'font-semibold' : 'text-tinta-2'}`} title={p.rotulo}>
              {p.rotulo}
            </span>
            <div className="relative h-5 rounded bg-fundo">
              <div className={`absolute inset-y-0 rounded ${cor}`} style={{ left: `${esq}%`, width: `${larg}%` }} />
            </div>
            <span className={`num text-right ${p.tipo === 'sobe' ? 'text-alerta' : p.tipo === 'desce' ? 'text-ok' : 'font-semibold'}`}>
              {p.tipo === 'total' ? reaisCompacto(p.ate) : sinal(p.ate - p.de)}
            </span>
          </div>
        )
      })}
      <p className="mt-2 text-xs text-tinta-3">Vermelho aumenta a carga, verde reduz. A soma dos fatores fecha exatamente na diferença.</p>
    </div>
  )
}

function Comparativo({ a, n, simples, ano }: { a: CargaDetalhada; n: CargaDetalhada; simples: ResultadoCenario['simples']; ano: number }) {
  const linhas: Array<[string, number, number]> = [
    ['PIS/COFINS', a.pisCofins, n.pisCofins],
    ['IPI', a.ipi, n.ipi],
    ['ICMS', a.icms, n.icms],
    ['ISS', a.iss, n.iss],
    ['CBS', a.cbs, n.cbs],
    ['IBS', a.ibs, n.ibs],
    ['DAS', a.das, n.das],
    ['(−) créditos', -a.creditos, -n.creditos],
  ]
  return (
    <div className="flex flex-col gap-3">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs text-tinta-3">
            <th className="pb-2 font-medium">Tributo</th>
            <th className="pb-2 text-right font-medium">Hoje</th>
            <th className="pb-2 text-right font-medium">{ano}</th>
          </tr>
        </thead>
        <tbody>
          {linhas
            .filter(([, x, y]) => x !== 0 || y !== 0)
            .map(([rotulo, x, y]) => (
              <tr key={rotulo} className="border-t border-linha">
                <td className="py-1.5">{rotulo}</td>
                <td className="num py-1.5 text-right">{reais(x)}</td>
                <td className="num py-1.5 text-right">{reais(y)}</td>
              </tr>
            ))}
          <tr className="border-t-2 border-linha font-semibold">
            <td className="py-1.5">Total</td>
            <td className="num py-1.5 text-right">{reais(a.total)}</td>
            <td className="num py-1.5 text-right">{reais(n.total)}</td>
          </tr>
        </tbody>
      </table>
      {simples && (
        <p className="text-xs text-tinta-2">
          Simples: alíquota efetiva {pctPpm(simples.efetivaAtual)} → {pctPpm(simples.efetivaNova)} (RBT12 {reaisCompacto(simples.rbt12)}). Dentro do DAS vão{' '}
          {reais(simples.cbsNoDas + simples.ibsNoDas)} de CBS/IBS — é só isso que o cliente empresa poderá creditar.
        </p>
      )}
    </div>
  )
}

function Meses({ c }: { c: ResultadoCenario }) {
  const meses = c.porCompetencia
  if (!meses.length) return <p className="text-sm text-tinta-3">Sem competências no ano-base.</p>
  const max = Math.max(1, ...meses.flatMap((m) => [m.debitoCbsIbs, m.creditoCbsIbs]))
  return (
    <div className="flex flex-col gap-3">
      <div className="flex h-44 items-end gap-2 overflow-x-auto">
        {meses.map((m) => {
          const saldo = m.debitoCbsIbs - m.creditoCbsIbs
          return (
            <div key={m.competencia} className="flex min-w-9 flex-1 flex-col items-center gap-1" title={`${mesCurto(m.competencia)} · débito ${reais(m.debitoCbsIbs)} · crédito ${reais(m.creditoCbsIbs)} · a recolher ${reais(saldo)}`}>
              <div className="flex h-36 w-full items-end justify-center gap-0.5">
                <div className="w-1/2 max-w-4 rounded-t bg-navy" style={{ height: `${(m.debitoCbsIbs / max) * 100}%` }} />
                <div className="w-1/2 max-w-4 rounded-t bg-ok" style={{ height: `${(m.creditoCbsIbs / max) * 100}%` }} />
              </div>
              <span className="num text-[10px] text-tinta-3">{mesCurto(m.competencia)}</span>
            </div>
          )
        })}
      </div>
      <div className="flex flex-wrap gap-4 text-xs text-tinta-2">
        <Legenda cor="bg-navy">débito de CBS/IBS nas vendas</Legenda>
        <Legenda cor="bg-ok">crédito nas compras</Legenda>
        <span className="text-tinta-3">Passe o mouse para ver o saldo do mês. Mês com crédito acima do débito acumula saldo credor.</span>
      </div>
    </div>
  )
}

function Legenda({ cor, children }: { cor: string; children: ReactNode }) {
  return (
    <span className="flex items-center gap-1.5">
      <span className={`inline-block h-2.5 w-2.5 rounded-sm ${cor}`} />
      {children}
    </span>
  )
}

function Qualidade({ r, c }: { r: SimulacaoResultado; c: ResultadoCenario }) {
  const q = r.qualidade
  const itens: Array<{ rotulo: string; valor: string; ruim: boolean }> = [
    { rotulo: 'Meses de notas no ano-base', valor: q.meses < 12 ? `${q.meses} (projetado ×${(q.fatorAnualizacao / 1_000_000).toFixed(2).replace('.', ',')})` : '12', ruim: q.meses < 12 },
    { rotulo: 'Receita sem classificação aprovada', valor: pctPpm(q.receitaSemClassificacao, 1), ruim: q.receitaSemClassificacao > 100_000 },
    { rotulo: 'Compras de fornecedor com regime presumido', valor: pctPpm(q.comprasRegimePresumido, 1), ruim: q.comprasRegimePresumido > 200_000 },
    { rotulo: 'Crédito que depende de premissa', valor: reais(c.creditoDependePremissa), ruim: c.creditoDependePremissa > 0 },
  ]
  return (
    <div className="flex flex-col gap-3">
      <dl className="flex flex-col gap-1.5 text-sm">
        {itens.map((i) => (
          <div key={i.rotulo} className="flex justify-between gap-3 border-b border-linha pb-1.5">
            <dt className="text-tinta-2">{i.rotulo}</dt>
            <dd className={`num ${i.ruim ? 'text-alerta' : 'text-tinta'}`}>{i.valor}</dd>
          </div>
        ))}
      </dl>
      {q.avisos.length > 0 && (
        <ul className="list-disc space-y-1 pl-5 text-xs text-tinta-2">
          {q.avisos.map((a) => (
            <li key={a}>{a}</li>
          ))}
        </ul>
      )}
    </div>
  )
}

function Acoes({ r, c, ir, staff }: { r: SimulacaoResultado; c: ResultadoCenario; ir: (t: Tela) => void; staff: boolean }) {
  const q = r.qualidade
  const creditoSimples = c.decomposicao.find((f) => f.fator === 'CREDITO_SIMPLES')?.valor ?? 0
  const acoes: Array<{ texto: string; tela?: Tela }> = []
  if (q.meses < 12) acoes.push({ texto: `Enviar os XMLs que faltam: só ${q.meses} mês(es) no ano-base — o resto é projeção.`, tela: staff ? 'enviar' : undefined })
  if (q.receitaSemClassificacao > 0)
    acoes.push({ texto: `Aprovar a classificação dos produtos: ${pctPpm(q.receitaSemClassificacao, 1)} da receita ainda usa sugestão ou alíquota cheia.`, tela: staff ? 'produtos' : undefined })
  if (q.comprasRegimePresumido > 0)
    acoes.push({ texto: `Confirmar o regime dos fornecedores: ${pctPpm(q.comprasRegimePresumido, 1)} das compras têm crédito presumido.`, tela: staff ? 'fornecedores' : 'pendencias' })
  if (creditoSimples > 0)
    acoes.push({ texto: `Fornecedores do Simples custam ${reais(creditoSimples)} de crédito que não passa. Vale negociar preço ou pedir que optem pelo regime híbrido.`, tela: staff ? 'credito' : undefined })
  if (c.simples) {
    const hoje = new Date().toISOString().slice(0, 7)
    const s = r.hibrido?.find((h) => h.ano === c.ano)?.opcao.semestres.find((x) => x.mesOpcao >= hoje)
    const prazo = s ? ` A opção para ${s.inicio.slice(5, 7)}/${s.inicio.slice(0, 4)} é em ${s.mesOpcao.slice(5, 7)}/${s.mesOpcao.slice(0, 4)}${s.cancelamentoAte ? ` e pode ser cancelada até ${s.cancelamentoAte.split('-').reverse().join('/')}` : ''}.` : ''
    acoes.push({ texto: `Decidir puro × híbrido: seus clientes empresa creditam só ${reais(c.simples.cbsNoDas + c.simples.ibsNoDas)} por ano do que você vende.${prazo}`, tela: 'simples' })
  }
  if (c.aliquotas.status === 'ESTIMATIVA' && !c.simples) acoes.push({ texto: 'Rodar de novo depois de 15/12/2026, quando o Senado fixar a alíquota de referência da CBS.' })
  if (c.variacao > 0) acoes.push({ texto: `Revisar preço de 2027: a carga sobe ${reais(c.variacao)} no ano com preços constantes.` })

  if (!acoes.length) return <p className="text-sm text-tinta-2">Base completa e sem ajuste pendente. Revise quando a alíquota oficial sair.</p>
  return (
    <ol className="flex list-decimal flex-col gap-2 pl-5 text-sm">
      {acoes.map((a) => (
        <li key={a.texto}>
          {a.texto}{' '}
          {a.tela && (
            <button className="text-navy underline" onClick={() => ir(a.tela!)}>
              abrir
            </button>
          )}
        </li>
      ))}
    </ol>
  )
}
